/**
 * gobdAccess — geteilte Zugriffslogik für die GoBD-Verfahrensdoku/-Export-Seite
 * (M9c, `/dashboard/gobd-export`).
 *
 * Single source of truth dafür, WER die GoBD-Export-Seite sehen und benutzen
 * darf. Wird von `config/navigation.ts` (Nav-Eintrag),
 * `routes/dashboardRoutePaths.ts` (Route-Registrierung) und
 * `pages/GobdExportPage.tsx` (Inhalts-Gate) gemeinsam genutzt — kein Copy-Paste
 * der Flag-/Allowlist-Bedingung. Muster: `config/travelExpensesAccess.ts` /
 * `config/receiptScanAccess.ts`.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-/Business-Account-UIDs sehen die
 *      Seite LIVE, unabhängig vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_GOBD_ENABLED==='true'`.
 *
 * KEIN Tier-Term. Jurist-Freigabe (2026-07-17): Beta-only rechtlich
 * unbedenklich, solange kein Dritter die Seite sieht — die tatsächlich
 * gerenderte Seite ist claim-arm. Die „GoBD-konform/revisionssicher"-Sätze in
 * den `gobd.json`-Locale-Dateien (`public/locales/<lang>/gobd.json`) bleiben von
 * KEINER Komponente gerendert und werden hier bewusst NICHT aktiviert; diese
 * Freischaltung fügt der UI KEINE neuen Rechts-Claim-Texte hinzu.
 *
 * Firebase-frei (nur `process.env`) — importierbar aus
 * `routes/dashboardRoutePaths.ts`/`config/navigation.ts`.
 *
 * @module config/gobdAccess
 */

/**
 * Beta-Erprobung GoBD-Export — hartkodiert (Muster `RECEIPT_SCAN_BETA_UIDS`),
 * für die kontrollierte Live-Erprobung durch Test- (b3DRR1…) und Business-
 * Account (70Tgnu…). Vor allgemeiner Freigabe wieder leeren.
 */
export const GOBD_BETA_UIDS: readonly string[] = [
  'b3DRR1Cj63cRjdJg5VCEUpBL7hk1',
  '70TgnuHwmDNI2LovagtcxXtIflo2',
];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet (dieselbe Env-Var, die
 * `GobdExportPage.tsx` bisher direkt als `GOBD_ENABLED` las).
 */
export const GOBD_FLAG: boolean = process.env.REACT_APP_GOBD_ENABLED === 'true';

/**
 * Route-Registrierungs-Gate (firebase-frei): die Route wird registriert, sobald
 * das Flag aktiv ist ODER die Beta-Allowlist nicht leer ist. Muster
 * `TRAVEL_EXPENSES_ROUTE_ENABLED`.
 */
export const GOBD_ROUTE_ENABLED: boolean = GOBD_FLAG || GOBD_BETA_UIDS.length > 0;

/**
 * Entscheidet fail-closed, ob ein Nutzer die GoBD-Export-Seite sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist. Sonst `false`.
 */
export function canUseGobd(uid: string | null | undefined): boolean {
  if (uid && GOBD_BETA_UIDS.includes(uid)) {
    return true;
  }
  return GOBD_FLAG;
}
